/**
 * Deciding whether a file is really audio the GPU can take, before uploading it.
 *
 * The server checks this too, and the server is the authority -- it sniffs the
 * bytes on completion and throws the file away if they disagree with the name.
 * This exists so a 200 MB file that was never going to be accepted does not
 * cross the network first.
 *
 * The signature table below mirrors `backend/app/uploads.py`. If one changes,
 * change both: a mismatch means either a file the browser rejects and the
 * server would have taken, or a wasted upload.
 */

export const ALLOWED_EXTENSIONS = ['mp3', 'aac', 'flac', 'wav', 'm4a'] as const
export type AudioKind = (typeof ALLOWED_EXTENSIONS)[number]

/** The GPU rejects anything larger, with an HTML 413. */
export const MAX_BYTES = 209_715_200

/** Enough for every signature, including the ftyp box at offset 4. */
const SNIFF_BYTES = 16

export function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.')
  return dot === -1
    ? ''
    : filename
        .slice(dot + 1)
        .toLowerCase()
        .trim()
}

/** Identify the container from its leading bytes, or null if unrecognised. */
export function sniffAudioKind(head: Uint8Array): AudioKind | null {
  if (head.length < 4) return null

  const ascii = (from: number, to: number) => String.fromCharCode(...head.slice(from, to))

  if (ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WAVE') return 'wav'
  if (ascii(0, 4) === 'fLaC') return 'flac'
  // Covers m4a and the rest of the MP4 family.
  if (ascii(4, 8) === 'ftyp') return 'm4a'
  if (ascii(0, 3) === 'ID3') return 'mp3'

  const [b0, b1] = [head[0], head[1]]
  if (b0 !== undefined && b1 !== undefined && b0 === 0xff && (b1 & 0xe0) === 0xe0) {
    // MPEG sync word, shared by MP3 and ADTS AAC. The two-bit layer field is
    // what separates them: layer 00 is what ADTS AAC uses, MP3 is layer 01.
    const layer = (b1 >> 1) & 0x03
    return layer === 0 ? 'aac' : 'mp3'
  }
  return null
}

/** The formats that genuinely overlap, so a disagreement is not a lie. */
const INTERCHANGEABLE: string[][] = [
  ['mp3', 'aac'],
  ['m4a', 'aac'],
]

export function kindsAreCompatible(extension: string, sniffed: AudioKind | null): boolean {
  if (sniffed === null) return false
  if (sniffed === extension) return true
  return INTERCHANGEABLE.some((group) => group.includes(extension) && group.includes(sniffed))
}

async function readHead(file: File): Promise<Uint8Array> {
  const buffer = await file.slice(0, SNIFF_BYTES).arrayBuffer()
  return new Uint8Array(buffer)
}

/**
 * How long the audio runs, or null if the browser cannot tell.
 *
 * Only metadata is loaded, so this does not decode the file. Being unable to
 * read a duration is itself a signal: a container the browser cannot parse is
 * unlikely to be one the GPU can either.
 */
/** Long enough for a real decode, short enough not to hold up a batch. */
export const PROBE_TIMEOUT_MS = 3_000

export function probeDuration(file: File): Promise<number | null> {
  // Anything unavailable here means "we do not know", never a thrown error:
  // the duration only feeds an estimate, and losing it must not stop a file
  // from being uploaded.
  if (typeof URL?.createObjectURL !== 'function' || typeof Audio !== 'function') {
    return Promise.resolve(null)
  }
  return new Promise((resolve) => {
    let url: string
    try {
      url = URL.createObjectURL(file)
    } catch {
      resolve(null)
      return
    }
    const audio = new Audio()
    const done = (value: number | null) => {
      URL.revokeObjectURL(url)
      audio.removeAttribute('src')
      resolve(value)
    }
    audio.preload = 'metadata'
    audio.onloadedmetadata = () =>
      done(Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : null)
    audio.onerror = () => done(null)
    // Some containers never fire either event in some browsers. Ten seconds was
    // longer than this is worth: the duration only sharpens an estimate, and
    // addFiles checks files one at a time, so a batch of twenty that all stall
    // would have held the whole queue for over three minutes before a single
    // byte went up.
    window.setTimeout(() => done(null), PROBE_TIMEOUT_MS)
    audio.src = url
  })
}

export interface FileCheck {
  ok: boolean
  /** Why not, in words a user can act on. */
  reason?: string
  kind?: AudioKind | null
  durationSeconds?: number | null
}

export async function checkAudioFile(file: File): Promise<FileCheck> {
  const extension = extensionOf(file.name)
  if (!(ALLOWED_EXTENSIONS as readonly string[]).includes(extension)) {
    return {
      ok: false,
      reason: `${extension || 'That file type'} is not accepted. Use ${ALLOWED_EXTENSIONS.join(', ')}.`,
    }
  }
  if (file.size === 0) {
    return { ok: false, reason: 'The file is empty.' }
  }
  if (file.size > MAX_BYTES) {
    const mb = Math.round(file.size / 1_048_576)
    return { ok: false, reason: `${mb} MB is over the 200 MB limit.` }
  }

  const kind = sniffAudioKind(await readHead(file))
  if (!kindsAreCompatible(extension, kind)) {
    return {
      ok: false,
      kind,
      reason:
        kind === null
          ? 'This does not look like an audio file.'
          : `This is named .${extension} but looks like ${kind}.`,
    }
  }

  const durationSeconds = await probeDuration(file)
  return { ok: true, kind, durationSeconds }
}
